import { useCallback, useEffect, useMemo, useState } from "react";
import { insertRow, listRows, updateRow } from "@/lib/adminApi";
import {
  PACKAGE_STATUS,
  commerceMeta,
  commerceOptions,
  type ExperiencePackage,
} from "@/lib/adminCommerce";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  TextField,
  btnGhost,
  btnPrimary,
  fieldInput,
  textInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatRupees } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

const EMPTY_PACKAGE = {
  name: "",
  description: "",
  included: "",
  price: 0,
  duration: "",
  capacity: 10,
  availability: "",
  terms: "",
  status: "draft",
};

export default function AdminPackages() {
  const [rows, setRows] = useState<ExperiencePackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_PACKAGE });
  const [saving, setSaving] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<ExperiencePackage>("experience_packages", "name"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load packages.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<ExperiencePackage>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("experience_packages", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const createPackage = async () => {
    if (!draft.name.trim()) {
      setError("A package name is required.");
      return;
    }
    setSaving(true);
    try {
      const created = await insertRow<ExperiencePackage>("experience_packages", {
        ...draft,
        name: draft.name.trim(),
      });
      setRows((list) => [created, ...list]);
      setCreateOpen(false);
      setDraft({ ...EMPTY_PACKAGE });
      notify(`${created.name} added to packages.`);
    } catch {
      setError("We couldn't create that package. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.name, row.included || "", row.availability || ""].join(" ").toLowerCase().includes(query);
    });
  }, [rows, search, statusFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const columns: Column<ExperiencePackage>[] = [
    {
      key: "name",
      header: "Package",
      primary: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-md overflow-hidden bg-background-100 shrink-0 flex items-center justify-center text-primary-700">
            {row.image ? (
              <img src={row.image} alt={row.name} className="w-full h-full object-cover object-top" />
            ) : (
              <i className="ri-archive-2-line"></i>
            )}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.name}</p>
            <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">{row.included || "—"}</p>
          </div>
        </div>
      ),
    },
    {
      key: "duration",
      header: "Duration",
      render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.duration || "—"}</span>,
    },
    {
      key: "price",
      header: "Price",
      render: (row) => (
        <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{formatRupees(row.price)}</span>
      ),
    },
    {
      key: "capacity",
      header: "Capacity",
      render: (row) => <span className="text-sm text-foreground-700">{row.capacity}</span>,
    },
    {
      key: "availability",
      header: "Availability",
      render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.availability || "—"}</span>,
    },
    { key: "status", header: "Status", render: (row) => <StatusPill {...commerceMeta(PACKAGE_STATUS, row.status)} /> },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Experiences"
        title="Packages"
        subtitle="Bundle several experiences into a single multi-day offer — the Weekend Farm Escape, school days and corporate retreats."
        actions={
          <button type="button" onClick={() => setCreateOpen(true)} className={btnPrimary}>
            <i className="ri-add-line"></i> New Package
          </button>
        }
      />

      <StatCards
        stats={[
          { label: "Total packages", value: rows.length, icon: "ri-archive-2-line", tone: "primary" },
          {
            label: "Published",
            value: rows.filter((row) => row.status === "published").length,
            icon: "ri-checkbox-circle-line",
            tone: "primary",
          },
          {
            label: "In review",
            value: rows.filter((row) => row.status === "review").length,
            icon: "ri-time-line",
            tone: "accent",
          },
          {
            label: "Avg. price",
            value: formatRupees(rows.length ? Math.round(rows.reduce((sum, row) => sum + Number(row.price || 0), 0) / rows.length) : 0),
            icon: "ri-price-tag-3-line",
            tone: "secondary",
          },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search packages…" />
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: "all", label: "All statuses" }, ...commerceOptions(PACKAGE_STATUS)]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} packages</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading packages…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-archive-2-line"
            title={rows.length === 0 ? "No packages yet" : "No packages match"}
            message="Multi-experience packages you create will appear here."
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.name || "Package"}
        subtitle="Package detail"
      >
        {selected ? (
          <>
            {selected.image ? (
              <div className="w-full h-40 rounded-lg overflow-hidden bg-background-100">
                <img src={selected.image} alt={selected.name} className="w-full h-full object-cover object-top" />
              </div>
            ) : null}

            <StatusPill {...commerceMeta(PACKAGE_STATUS, selected.status)} />

            {selected.description ? (
              <p className="text-sm text-foreground-700 leading-relaxed">{selected.description}</p>
            ) : null}

            <DetailList
              items={[
                { icon: "ri-time-line", label: "Duration", value: selected.duration || "—" },
                { icon: "ri-price-tag-3-line", label: "Price", value: formatRupees(selected.price) },
                { icon: "ri-group-line", label: "Capacity", value: `${selected.capacity} guests` },
                { icon: "ri-calendar-check-line", label: "Availability", value: selected.availability || "—" },
              ]}
            />

            {selected.included ? (
              <Field label="Included experiences">
                <div className="flex flex-wrap gap-2">
                  {selected.included.split(",").map((item) => (
                    <span
                      key={item}
                      className="text-xs font-semibold text-accent-900 bg-accent-100 border border-accent-300 px-2.5 py-1 rounded-full"
                    >
                      {item.trim()}
                    </span>
                  ))}
                </div>
              </Field>
            ) : null}

            {selected.terms ? (
              <Field label="Terms">
                <p className="text-sm text-foreground-700 leading-relaxed">{selected.terms}</p>
              </Field>
            ) : null}

            <Field label="Publication status">
              <select
                value={selected.status}
                onChange={(event) => update(selected.id, { status: event.target.value }, "Package status updated.")}
                className={fieldInput}
              >
                {commerceOptions(PACKAGE_STATUS).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create a new package"
        subtitle="New record"
        footer={
          <>
            <button type="button" onClick={() => setCreateOpen(false)} className={btnGhost}>
              Cancel
            </button>
            <button type="button" onClick={createPackage} disabled={saving} className={btnPrimary}>
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
              Create package
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <TextField label="Package name">
              <input
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                placeholder="e.g. Weekend Farm Escape"
                className={textInput}
              />
            </TextField>
          </div>
          <TextField label="Price (₹)">
            <input
              type="number"
              value={draft.price}
              onChange={(event) => setDraft({ ...draft, price: Number(event.target.value) })}
              className={textInput}
            />
          </TextField>
          <TextField label="Capacity (guests)">
            <input
              type="number"
              value={draft.capacity}
              onChange={(event) => setDraft({ ...draft, capacity: Number(event.target.value) })}
              className={textInput}
            />
          </TextField>
          <TextField label="Duration">
            <input
              value={draft.duration}
              onChange={(event) => setDraft({ ...draft, duration: event.target.value })}
              placeholder="e.g. 2 days / 1 night"
              className={textInput}
            />
          </TextField>
          <TextField label="Availability">
            <input
              value={draft.availability}
              onChange={(event) => setDraft({ ...draft, availability: event.target.value })}
              placeholder="e.g. Weekends"
              className={textInput}
            />
          </TextField>
          <TextField label="Status">
            <select
              value={draft.status}
              onChange={(event) => setDraft({ ...draft, status: event.target.value })}
              className={fieldInput}
            >
              {commerceOptions(PACKAGE_STATUS).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </TextField>
          <TextField label="Included experiences">
            <input
              value={draft.included}
              onChange={(event) => setDraft({ ...draft, included: event.target.value })}
              placeholder="Comma separated, e.g. Paddy Walk, Farm Meal"
              className={textInput}
            />
          </TextField>
          <div className="sm:col-span-2">
            <TextField label="Description">
              <textarea
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                rows={2}
                maxLength={500}
                placeholder="A short summary of the package…"
                className={`${textInput} resize-none`}
              ></textarea>
            </TextField>
          </div>
          <div className="sm:col-span-2">
            <TextField label="Terms">
              <textarea
                value={draft.terms}
                onChange={(event) => setDraft({ ...draft, terms: event.target.value })}
                rows={2}
                maxLength={500}
                placeholder="Booking terms and cancellation policy…"
                className={`${textInput} resize-none`}
              ></textarea>
            </TextField>
          </div>
        </div>
      </Modal>
      {node}
    </div>
  );
}